import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Wrench,
  CalendarClock,
  ClipboardList,
  Package,
  HardHat,
  FolderKanban,
  FileText,
  Receipt,
  Users,
  BarChart3,
  Settings,
  Inbox,
  KeyRound,
  Clock3,
  CalendarDays,
  ShieldCheck,
  History,
  X,
  ChevronDown,
  Box,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { canAccessPath } from '../../lib/permissions';

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  group: 'daily' | 'customers' | 'field' | 'administration' | 'account';
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <LayoutDashboard className="h-4 w-4" />, group: 'daily' },
  { label: 'Simulator', to: '/simulator', icon: <Box className="h-4 w-4" />, group: 'daily' },
  { label: 'Customer Requests', to: '/inbox', icon: <Inbox className="h-4 w-4" />, group: 'daily' },
  { label: 'Work Orders', to: '/work-orders', icon: <ClipboardList className="h-4 w-4" />, group: 'daily' },
  { label: 'Dispatch', to: '/dispatch', icon: <CalendarDays className="h-4 w-4" />, group: 'daily' },
  { label: 'Time Clock', to: '/time-clock', icon: <Clock3 className="h-4 w-4" />, group: 'daily' },
  { label: 'Clients', to: '/clients', icon: <Users className="h-4 w-4" />, group: 'customers' },
  { label: 'Quotes', to: '/quotes', icon: <FileText className="h-4 w-4" />, group: 'customers' },
  { label: 'Projects', to: '/projects', icon: <FolderKanban className="h-4 w-4" />, group: 'customers' },
  { label: 'Invoices', to: '/invoices', icon: <Receipt className="h-4 w-4" />, group: 'customers' },
  { label: 'Assets', to: '/assets', icon: <Wrench className="h-4 w-4" />, group: 'field' },
  { label: 'PM Schedule', to: '/pm', icon: <CalendarClock className="h-4 w-4" />, group: 'field' },
  { label: 'Inventory', to: '/inventory', icon: <Package className="h-4 w-4" />, group: 'field' },
  { label: 'Technicians', to: '/technicians', icon: <HardHat className="h-4 w-4" />, group: 'field' },
  { label: 'Reports', to: '/reports', icon: <BarChart3 className="h-4 w-4" />, group: 'administration' },
  { label: 'Settings / Users', to: '/settings/users', icon: <Settings className="h-4 w-4" />, group: 'administration' },
  {
    label: 'Role Permissions',
    to: '/settings/permissions',
    icon: <ShieldCheck className="h-4 w-4" />,
    group: 'administration',
  },
  { label: 'Audit Log', to: '/settings/audit-log', icon: <History className="h-4 w-4" />, group: 'administration' },
  {
    label: 'Change Password',
    to: '/settings/password',
    icon: <KeyRound className="h-4 w-4" />,
    group: 'account',
  },
];

const NAV_GROUPS = [
  { id: 'daily', label: 'Daily work', defaultOpen: true },
  { id: 'customers', label: 'Customers & sales', defaultOpen: true },
  { id: 'field', label: 'Field tools', defaultOpen: false },
  { id: 'administration', label: 'Administration', defaultOpen: false },
  { id: 'account', label: 'My account', defaultOpen: false },
] as const;

interface SidebarProps {
  role?: string;
  isOfflineSession?: boolean;
  onClose?: () => void;
}

interface OnlineStaff {
  id: string;
  fullName: string;
  role: string;
  online: boolean;
  lastSeenAt: string | null;
}

function lastSeenLabel(value: string | null): string {
  if (!value) return 'No activity yet';
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 60_000));
  if (minutes < 2) return 'Last active just now';
  if (minutes < 60) return `Last active ${minutes}m ago`;
  if (minutes < 24 * 60) return `Last active ${Math.floor(minutes / 60)}h ago`;
  return `Last active ${new Date(value).toLocaleDateString()}`;
}

export function Sidebar({ role, isOfflineSession, onClose }: SidebarProps) {
  const location = useLocation();
  const visibleItems = NAV_ITEMS.filter((item) => canAccessPath(role, item.to));
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(
    () => Object.fromEntries(NAV_GROUPS.map((group) => [group.id, group.defaultOpen])),
  );

  // Live unread count for Customer Requests badge
  const { data: countData } = useQuery<{ count: number }>({
    queryKey: ['customer-requests-count'],
    queryFn: async () => { const { data } = await api.get('/customer-requests/count'); return data; },
    refetchInterval: 30_000,
    enabled: canAccessPath(role, '/inbox'),
  });
  const newRequestCount = countData?.count ?? 0;
  const { data: presence, isError: presenceError } = useQuery<{ users: OnlineStaff[] }>({
    queryKey: ['presence-online'],
    queryFn: async () => { const { data } = await api.get('/presence/online'); return data; },
    refetchInterval: 30_000,
    enabled: !isOfflineSession,
  });
  const staff = [...(presence?.users ?? [])].sort((a, b) =>
    Number(b.online) - Number(a.online) || a.fullName.localeCompare(b.fullName));
  const onlineCount = staff.filter((person) => person.online).length;

  return (
    <aside className="flex h-full w-60 flex-col bg-gray-900 text-gray-100">
      {/* Logo / brand */}
      <div className="flex h-20 items-center justify-between px-4 border-b border-gray-700">
        <div className="flex items-center rounded-xl bg-white px-3 py-1.5">
          <img
            src="/logo.jpg"
            alt="Hold Engineering"
            className="h-11 w-auto object-contain"
          />
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:text-white lg:hidden"
            aria-label="Close sidebar"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        <div className="space-y-3">
          {NAV_GROUPS.map((group) => {
            const items = visibleItems.filter((item) => item.group === group.id);
            if (items.length === 0) return null;

            const containsCurrentPage = items.some((item) => location.pathname === item.to);
            const isOpen = openGroups[group.id] || containsCurrentPage;

            return (
              <section key={group.id} aria-label={group.label}>
                <button
                  type="button"
                  onClick={() =>
                    setOpenGroups((current) => ({ ...current, [group.id]: !current[group.id] }))
                  }
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                >
                  {group.label}
                  <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', isOpen && 'rotate-180')} />
                </button>
                {isOpen && (
                  <ul className="mt-1 space-y-0.5">
                    {items.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                              isActive
                                ? 'bg-primary text-white'
                                : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                            )
                          }
                          onClick={onClose}
                        >
                          {item.icon}
                          {item.label}
                          {item.to === '/inbox' && newRequestCount > 0 && (
                            <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs font-bold text-white">
                              {newRequestCount > 9 ? '9+' : newRequestCount}
                            </span>
                          )}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      </nav>
      <section aria-label="Online staff" className="border-t border-gray-700 px-4 py-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Team presence · {onlineCount} online
        </h2>
        {isOfflineSession ? <p className="mt-2 text-xs text-gray-400">Unavailable offline</p>
          : presenceError ? <p className="mt-2 text-xs text-gray-400">Status unavailable</p>
          : staff.length ? (
            <ul className="mt-2 max-h-44 space-y-2 overflow-y-auto">
              {staff.map((person) => (
                <li key={person.id} className="flex items-start gap-2 text-xs text-gray-200">
                  <span aria-hidden="true" className={cn('mt-1 h-2 w-2 shrink-0 rounded-full', person.online ? 'bg-green-400' : 'bg-gray-500')} />
                  <span className="min-w-0">
                    <span className="block truncate" title={person.fullName}>{person.fullName}</span>
                    <span className="block text-[11px] text-gray-400" title={person.lastSeenAt ? new Date(person.lastSeenAt).toLocaleString() : undefined}>
                      {person.online ? 'Online now' : lastSeenLabel(person.lastSeenAt)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          ) : <p className="mt-2 text-xs text-gray-400">No staff accounts found</p>}
      </section>
    </aside>
  );
}
